import { EntityStatusChip } from '../entity-detail/EntityStatusChip.js';
import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { ListTodo } from 'lucide-react';
import { Typography } from 'antd';

import { useTasks } from '../../features/tasks/hooks/useTasks.js';
import { formatDateTime, STATUS_TONE } from '../../features/tasks/lib/taskMeta.js';
import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import type { TaskStatus } from '../../features/tasks/api/tasks.client.js';

const { Text } = Typography;

export interface OpenTasksWidgetProps {
  className?: string;
  testId?: string;
  /** Показать первые N открытых задач. */
  limit?: number;
  onItemClick?: (task: { id: string }) => void;
  onViewAll?: () => void;
}

const OPEN_STATUSES: ReadonlySet<TaskStatus> = new Set(['todo', 'in_progress', 'blocked']);

/** Открытые задачи: кворк делает видимым то, что ещё не закрыто. */
export function OpenTasksWidget({
  className,
  testId,
  limit = 8,
  onItemClick,
  onViewAll,
}: OpenTasksWidgetProps): ReactElement {
  const { t } = useTranslation();
  const { data, isLoading } = useTasks();
  // Сервер отдаёт до 200 задач по статусу/сроку; открытые выбираем здесь —
  // отдельный запрос на каждый открытый статус был бы дороже.
  const openTasks = (data ?? [])
    .filter((task) => OPEN_STATUSES.has(task.status))
    .sort((a, b) => {
      const aDue = a.dueAt ? new Date(a.dueAt).getTime() : Number.MAX_SAFE_INTEGER;
      const bDue = b.dueAt ? new Date(b.dueAt).getTime() : Number.MAX_SAFE_INTEGER;
      return aDue - bDue || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    })
    .slice(0, limit);

  const widgetContainerProps = {
    title: t('crm.dashboard.open_tasks.title'),
    icon: ListTodo,
    minHeight: 300,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <button
        type="button"
        onClick={onViewAll}
        className="border-0 bg-transparent text-xs"
        style={{ color: 'var(--ant-color-primary)', cursor: onViewAll ? 'pointer' : 'default' }}
      >
        {t('crm.dashboard.open_tasks.view_all')}
      </button>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {isLoading ? (
        <div className="animate-pulse p-3.5 flex flex-col gap-1.5">
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              className="crm-radius-s h-10"
              style={{ background: 'var(--g-fill, #f5f1eb)' }}
            />
          ))}
        </div>
      ) : openTasks.length === 0 ? (
        <div
          className="flex items-center justify-center gap-2"
          style={{ padding: '18px 16px', opacity: 0.6 }}
        >
          <ListTodo size={16} style={{ color: 'var(--ant-color-text-tertiary)' }} />
          <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
            {t('crm.dashboard.open_tasks.empty')}
          </span>
        </div>
      ) : (
        <ul className="m-0 list-none p-0">
          {openTasks.map((task) => (
            <li key={task.id}>
              <button
                type="button"
                onClick={onItemClick ? () => onItemClick(task) : undefined}
                className="transition-colors text-left border-0 flex w-full items-start gap-2 bg-transparent crm-hover-fill"
                style={{
                  cursor: onItemClick ? 'pointer' : 'default',
                  padding: '4px 14px',
                  minHeight: 42,
                  borderBottom: '1px solid var(--ant-color-border-secondary)',
                }}
              >
                <div className="flex-1 min-w-0 py-1">
                  <Text
                    strong
                    className="truncate block"
                    style={{ fontSize: 12.5, color: 'var(--ant-color-text)', lineHeight: 1.3 }}
                  >
                    {task.title}
                  </Text>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, color: 'var(--ant-color-text-secondary)' }}
                  >
                    {task.dueAt ? formatDateTime(task.dueAt) : t('crm.dashboard.open_tasks.no_due')}
                  </Text>
                </div>
                <EntityStatusChip tone={STATUS_TONE[task.status]} style={{ marginInlineEnd: 0 }}>
                  {t(`crm.dashboard.open_tasks.status.${task.status}`)}
                </EntityStatusChip>
              </button>
            </li>
          ))}
        </ul>
      )}
    </WidgetContainer>
  );
}
